import { AnimatePresence, motion } from 'motion/react';
import QRBorder from '../assets/images/QR-Code-Border.svg';
import QRModalBackground from '../assets/images/qr-modal-background.png';
import QRArrowDown from '../assets/images/qr-arrow-down.png';
import QRArrowRight from '../assets/images/qr-arrow-right.png';
import QRPhone from '../assets/images/QR-Code-Phone.svg';
import QRCodeSVG from 'qrcode-svg-ts';
import { useEffect, useRef, useState } from 'react';
import CaretUpIcon from './Icons/CaretUpIcon';
import CloseIcon from './Icons/CloseIcon';
import ZoomInviteButton from './Zoom/ZoomInviteButton';
import { playerSessionUrl, sessionIdFromPath } from '@/utils/sessionLinks';

const getQrCodeDataUrl = (url: string) => new QRCodeSVG(url).toDataURL();

/**
 * Resolves the player join URL from the route rather than by string-replacing the
 * current href, which silently produced wrong links on any non-`/host` path.
 */
const usePlayerUrl = () => {
  const sessionId = sessionIdFromPath();
  return sessionId ? playerSessionUrl(sessionId) : '';
};

function QRCodeIcon() {
  const sessionUrl = usePlayerUrl();
  const qrCodeSvg = getQrCodeDataUrl(sessionUrl);

  return <img src={qrCodeSvg} className="w-full h-full" alt="QR Code" />;
}

export function QRCodeMiniSection() {
  return (
    <div className="flex items-center justify-between pt-18 pb-12 px-16 bg-secondary-sky-blue-100 gap-16 rounded-[24px]">
      <p className="text-body-sm">
        Missed the start? Join in now!
        <br />
        Ask host to expand QR code.
      </p>
      <div className="w-[47px] relative">
        <CaretUpIcon className="absolute -top-20 left-11 text-primary-500" />
        <QRCodeIcon />
      </div>
    </div>
  );
}

export function QRSection({
  setGameLinkCopied,
  compact = false
}: {
  setGameLinkCopied: React.Dispatch<React.SetStateAction<boolean>>;
  /** Narrow-panel variant used inside the Zoom app (desktop panel and Zoom Web App). */
  compact?: boolean;
}) {
  const sessionUrl = usePlayerUrl();
  const qrCodeSvg = getQrCodeDataUrl(sessionUrl);
  const [copied, setCopied] = useState(false);

  const handleCopyToClipboard = async () => {
    setGameLinkCopied(true);

    // The Zoom webview does not always grant clipboard access; fall back silently
    // rather than leaving the click with no effect.
    try {
      await navigator.clipboard.writeText(sessionUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (compact) {
    // Panel-sized variant: no fixed 384px frame, no decorative phone illustration,
    // and the Zoom invite promoted above the link fallback.
    return (
      <motion.div
        className="flex w-full flex-col items-center gap-12 px-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <p className="text-body text-body-strong text-center text-[#1D1A34]">
          Invite people to join this game
        </p>
        <img src={qrCodeSvg} className="w-160 h-160" alt="QR code to join the game" />
        <p className="text-body text-body-sm text-secondary-navy-500 text-center">
          Scan with a phone, or share the link.
        </p>
        <div className="flex w-full flex-col gap-8">
          <ZoomInviteButton className="btn btn-sm btn-primary w-full" />
          <button className="btn btn-sm btn-neutral w-full" onClick={handleCopyToClipboard}>
            {!copied ? 'Copy Game Link' : 'Copied'}
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="flex flex-col justify-center items-center h-full ml-40 px-40"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <motion.span
        className="text-[#1D1A34] text-h2 w-233 text-center ml-13"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.3 }}
      >
        Scan the QR code on your phone to join
      </motion.span>
      <div className="relative flex justify-center items-center w-384 h-320 mt-32">
        <motion.img
          src={QRBorder}
          className="absolute w-full h-full"
          alt="QR Code Border"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.3, type: 'spring', stiffness: 100 }}
        />
        <motion.img
          src={qrCodeSvg}
          className="absolute w-224 h-224"
          alt="QR Code"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.3, type: 'spring', stiffness: 100 }}
        />
        <motion.img
          src={QRPhone}
          className="absolute w-100 h-100 -top-96 -left-2"
          alt="QR Code"
          initial={{ x: 50, y: 50, rotate: 25, opacity: 0 }}
          animate={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.3, type: 'spring', stiffness: 100 }}
        />
      </div>
      <motion.div
        className="mt-20 flex justify-between gap-12"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1, duration: 0.3 }}
      >
        <div>
          <p className="text-body text-body-sm-strong text-secondary-navy-500">
            Having trouble scanning?
          </p>
          <p className="text-body text-body-sm text-secondary-navy-500">
            Ask the host to share the link.
          </p>
        </div>
        <div className="flex flex-col gap-8">
          <ZoomInviteButton className="btn btn-sm btn-primary min-w-139" />
          <button className="btn btn-sm btn-neutral min-w-139" onClick={handleCopyToClipboard}>
            {!copied ? 'Copy Game Link' : 'Copied'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function QRModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const sessionUrl = usePlayerUrl();
  const qrCodeSvg = getQrCodeDataUrl(sessionUrl);
  const [copied, setCopied] = useState(false);
  const modalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleClickOutside = (event: Event) => {
      const target = event.target as Node | null;

      if (!modalRef.current || !target) {
        return;
      }

      if (!modalRef.current.contains(target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [open, onClose]);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(sessionUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="w-full absolute flex justify-center -top-256 left-0 z-100 h-342">
          <motion.div
            ref={modalRef}
            className="flex justify-between items-center w-700 py-40 px-65 gap-30 border-2 border-primary-500 rounded-sm absolute bg-cover"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            style={{ backgroundImage: `url(${QRModalBackground})` }}
          >
            <CloseIcon
              className="size-24 absolute right-24 top-24 cursor-pointer"
              onClick={onClose}
            />
            <div className="w-276 shrink-0 relative">
              <img src={QRArrowRight} className="absolute w-57 left-240 -top-50" />
              <p className="font-24/25 text-center mb-40 font-black w-full">
                Scan the QR code on your phone to join
              </p>
              <div className="flex flex-col justify-between gap-12 justify-center items-center">
                <div className="text-center relative">
                  <img src={QRArrowDown} className="absolute w-57 left-160 top-15" />
                  <p className="text-body text-body-sm-strong text-secondary-navy-500">
                    Having trouble scanning?
                  </p>
                  <p className="text-body text-body-sm text-secondary-navy-500">
                    Ask the host to share the link.
                  </p>
                </div>
                <button
                  className="btn-game btn-neutral btn-sm w-160"
                  onClick={handleCopyToClipboard}
                >
                  {!copied ? 'Copy Game Link' : 'Copied'}
                </button>
              </div>
            </div>
            <div className="relative flex justify-center items-center w-263 h-260">
              <img src={QRBorder} className="absolute w-full h-full" alt="QR Code Border" />
              <img src={qrCodeSvg} className="absolute w-188 h-188" alt="QR Code" />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
